import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GitCompareArrows, Loader2, ArrowRight, Building2, Brain, Download, Gauge, Wand2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';

const STATUS_STYLE = {
  identic: { c: 'bg-emerald-500/15 text-emerald-400', l: 'Identic' },
  diferit: { c: 'bg-amber-500/15 text-amber-400', l: 'Diferit' },
  doar_a: { c: 'bg-blue-500/15 text-blue-400', l: 'Doar A' },
  doar_b: { c: 'bg-purple-500/15 text-purple-400', l: 'Doar B' },
};

function SourcePicker({ label, projects, kbDocs, value, onChange, testid }) {
  return (
    <div className="flex-1 p-4 rounded-lg bg-zinc-900/40 border border-white/10" data-testid={testid}>
      <p className="text-xs font-semibold text-primary mb-2" style={{ fontFamily: 'Outfit' }}>{label}</p>
      <Select value={value.type} onValueChange={t => onChange({ type: t, id: '' })}>
        <SelectTrigger className="bg-zinc-900/50 border-white/10 h-9 text-xs mb-2" data-testid={`${testid}-type`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="project">Licitatie proprie</SelectItem>
          <SelectItem value="kb">Licitatie castigata (KB)</SelectItem>
        </SelectContent>
      </Select>
      <Select value={value.id} onValueChange={id => onChange({ ...value, id })}>
        <SelectTrigger className="bg-zinc-900/50 border-white/10 h-9 text-xs" data-testid={`${testid}-item`}>
          <SelectValue placeholder={value.type === 'kb' ? 'Alege document KB...' : 'Alege licitatie...'} />
        </SelectTrigger>
        <SelectContent>
          {value.type === 'kb'
            ? kbDocs.filter(d => d.status === 'ready').map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)
            : projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="mt-2 flex items-center gap-1.5 text-[10px] text-muted-foreground">
        {value.type === 'kb' ? <Brain className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
        {value.type === 'kb' ? 'Din baza de cunostinte ML' : 'Documentele autoritatii din proiect'}
      </div>
    </div>
  );
}

export default function ComparatorPage() {
  const [projects, setProjects] = useState([]);
  const [kbDocs, setKbDocs] = useState([]);
  const [srcA, setSrcA] = useState({ type: 'project', id: '' });
  const [srcB, setSrcB] = useState({ type: 'project', id: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    API.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    API.get('/kb/documents').then(r => setKbDocs(r.data)).catch(() => {});
  }, []);

  const compare = async () => {
    if (!srcA.id || !srcB.id) { toast.error('Alege ambele licitatii'); return; }
    setLoading(true); setResult(null);
    try {
      const r = await API.post('/compare-tenders', { source_a: srcA, source_b: srcB });
      setResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare comparare'); }
    finally { setLoading(false); }
  };

  const exportComparison = async (format) => {
    if (!result) return;
    try {
      const res = await API.post(`/export/comparison?format=${format}`, result, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `comparatie_cerinte.${format}`; a.click();
      toast.success(`Export ${format.toUpperCase()} OK`);
    } catch { toast.error('Eroare export'); }
  };

  const acc = result?.accesibilitate;
  const scoreColor = (s) => s >= 70 ? 'text-emerald-400' : s >= 40 ? 'text-amber-400' : 'text-rose-400';

  const [reuseLoading, setReuseLoading] = useState(false);
  const [reuseResult, setReuseResult] = useState(null);
  const targetProjectId = srcA.type === 'project' ? srcA.id : (srcB.type === 'project' ? srcB.id : '');
  const kbReference = srcA.type === 'kb' ? { type: 'kb', id: srcA.id } : (srcB.type === 'kb' ? { type: 'kb', id: srcB.id } : null);

  const reuseSolutions = async () => {
    if (!targetProjectId) { toast.error('Alege o licitatie proprie (cu documente) ca tinta'); return; }
    setReuseLoading(true); setReuseResult(null);
    try {
      const r = await API.post('/kb/reuse-solutions', { project_id: targetProjectId, reference: kbReference || undefined });
      setReuseResult(r.data); toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare preluare solutii'); }
    finally { setReuseLoading(false); }
  };

  return (
    <div className="p-8" data-testid="comparator-page">
      <div className="flex items-center gap-3 mb-2">
        <GitCompareArrows className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>Comparator Cerinte</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Compara semantic cerintele a doua licitatii (proprii sau castigate) si vezi rapid diferentele.</p>

      <Card className="bg-card border-white/10 mb-6">
        <CardContent className="p-5">
          <div className="flex flex-col md:flex-row items-stretch gap-3">
            <SourcePicker label="Licitatia A" projects={projects} kbDocs={kbDocs} value={srcA} onChange={setSrcA} testid="source-a" />
            <div className="flex items-center justify-center"><ArrowRight className="w-5 h-5 text-muted-foreground rotate-90 md:rotate-0" /></div>
            <SourcePicker label="Licitatia B" projects={projects} kbDocs={kbDocs} value={srcB} onChange={setSrcB} testid="source-b" />
          </div>
          <Button onClick={compare} disabled={loading} className="w-full mt-4" data-testid="compare-btn">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Se compara (AI)...</> : <><GitCompareArrows className="w-4 h-4 mr-2" />Compara Cerintele</>}
          </Button>
          <Button onClick={reuseSolutions} disabled={reuseLoading || !targetProjectId} variant="outline" className="w-full mt-2 border-primary/30 text-primary hover:bg-primary/10" data-testid="reuse-btn">
            {reuseLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Se preiau solutiile...</> : <><Wand2 className="w-4 h-4 mr-2" />Preia Solutiile Castigate (metodologie + devize C6-C9)</>}
          </Button>
          <p className="text-[10px] text-muted-foreground mt-1.5 text-center">Preia si adapteaza solutiile din licitatia castigata cea mai similara in proiectul tinta {targetProjectId ? '' : '(alege o licitatie proprie)'}</p>
        </CardContent>
      </Card>

      {reuseResult && (
        <Card className="bg-card border-primary/20 mb-6" data-testid="reuse-result">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Wand2 className="w-4 h-4 text-primary" />Solutii Preluate (salvate in Formulare)</CardTitle>
            <p className="text-[11px] text-muted-foreground">Referinta: {reuseResult.reference_title}</p>
          </CardHeader>
          <CardContent>
            <div className="prose-dark border border-white/5 rounded-lg p-4 max-h-[500px] overflow-auto bg-zinc-900/30"><ReactMarkdown>{reuseResult.content}</ReactMarkdown></div>
          </CardContent>
        </Card>
      )}

      {result && (
        <div className="space-y-6" data-testid="comparator-result">
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => exportComparison('docx')} className="text-xs h-8" data-testid="export-comp-docx"><Download className="w-3.5 h-3.5 mr-1.5" />Word</Button>
            <Button size="sm" variant="outline" onClick={() => exportComparison('pdf')} className="text-xs h-8" data-testid="export-comp-pdf"><Download className="w-3.5 h-3.5 mr-1.5" />PDF</Button>
          </div>

          {acc && (
            <Card className="bg-card border-white/10" data-testid="accessibility-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Gauge className="w-4 h-4 text-primary" />Accesibilitate (model 7-factori)</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  {[{ k: 'a', label: result.label_a }, { k: 'b', label: result.label_b }].map(({ k, label }) => (
                    <div key={k} className="p-3 rounded-lg bg-zinc-900/50 border border-white/5 text-center">
                      <p className="text-[10px] text-muted-foreground mb-1">Licitatia {k.toUpperCase()}</p>
                      <p className={`text-3xl font-bold font-mono-data ${scoreColor(acc[k]?.scor || 0)}`} data-testid={`accessibility-score-${k}`}>{acc[k]?.scor ?? '-'}%</p>
                      <p className="text-[10px] text-muted-foreground mt-1 leading-tight">{acc[k]?.detalii}</p>
                    </div>
                  ))}
                </div>
                {acc.verdict && <p className="text-sm p-3 rounded bg-primary/5 border border-primary/10"><span className="font-semibold text-primary">Verdict:</span> {acc.verdict}</p>}
              </CardContent>
            </Card>
          )}

          {result.rezumat && (
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4">
                <p className="text-xs font-semibold text-primary mb-1" style={{ fontFamily: 'Outfit' }}>Rezumat comparativ</p>
                <p className="text-sm">{result.rezumat}</p>
              </CardContent>
            </Card>
          )}
          {result.diferente_cheie?.length > 0 && (
            <Card className="bg-card border-white/10">
              <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ fontFamily: 'Outfit' }}>Diferente Cheie</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {result.diferente_cheie.map((d, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm"><span className="text-amber-400">•</span><span>{d}</span></div>
                ))}
              </CardContent>
            </Card>
          )}
          <Card className="bg-card border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm" style={{ fontFamily: 'Outfit' }}>Cerinte Aliniate</CardTitle>
              <p className="text-[11px] text-muted-foreground">A: {result.label_a} · B: {result.label_b}</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-white/10 text-[10px] text-muted-foreground uppercase">
                    <th className="text-left py-2 pr-3 w-40">Categorie</th>
                    <th className="text-left py-2 pr-3">Cerinta A</th>
                    <th className="text-left py-2 pr-3">Cerinta B</th>
                    <th className="text-center py-2 w-20">Status</th>
                    <th className="text-left py-2">Diferenta</th>
                  </tr></thead>
                  <tbody>
                    {(result.aliniate || []).map((row, i) => {
                      const st = STATUS_STYLE[row.status] || STATUS_STYLE.diferit;
                      return (
                        <tr key={i} className="border-b border-white/5 align-top" data-testid={`compare-row-${i}`}>
                          <td className="py-2 pr-3 text-xs font-medium">{row.categorie}</td>
                          <td className="py-2 pr-3 text-xs text-muted-foreground">{row.cerinta_a || '—'}</td>
                          <td className="py-2 pr-3 text-xs text-muted-foreground">{row.cerinta_b || '—'}</td>
                          <td className="py-2 text-center"><Badge className={`text-[9px] ${st.c}`}>{st.l}</Badge></td>
                          <td className="py-2 text-xs text-amber-300/90">{row.diferenta || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {!result.aliniate?.length && <p className="text-xs text-muted-foreground text-center py-4">Nu s-au putut alinia cerintele</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
